import { Inject, Injectable } from '@angular/core';
import { ApiService } from '@improver/core';
import { mapApiResponseItems } from '@improver/core/operators';
import { BehaviorSubject, MonoTypeOperatorFunction, Observable, forkJoin, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { EMPLOYMENT_ACCESS_ENDPOINTS, EmploymentAccessApiEndpoints } from './../employment-access.endpoints';
import { EmploymentAccess } from './../employment-access.model';

const findPermissionMap = (employmentAccesses: EmploymentAccess[]) => {
  const findMap = (data: EmploymentAccess[]) => data.find(({ employmentId }) => employmentId === 0)?.permissions;
  const lastDataItem = employmentAccesses[employmentAccesses.length - 1];

  return lastDataItem.employmentId === 0 ? lastDataItem.permissions : findMap(employmentAccesses);
};

const transformManagedEmploymentsMap = (data: EmploymentAccess[]) => {
  const permissions = findPermissionMap(data);

  return data
    .filter((item: EmploymentAccess) => item.employmentId !== 0)
    .map((item: EmploymentAccess) => ({ ...item, permissions: item.permissions.map((permissionId) => permissions[permissionId]) }));
};

@Injectable({
  providedIn: 'root',
})
export class EmploymentAccessDataFetcherService {
  private readonly managedEmploymentsAccessSubject: BehaviorSubject<EmploymentAccess[]> = new BehaviorSubject<EmploymentAccess[]>([]);
  private readonly myEmploymentsAccessSubject: BehaviorSubject<EmploymentAccess[]> = new BehaviorSubject<EmploymentAccess[]>([]);

  managedEmploymentsAccess$: Observable<EmploymentAccess[]> = this.managedEmploymentsAccessSubject.asObservable();
  myEmploymentsAccess$: Observable<EmploymentAccess[]> = this.myEmploymentsAccessSubject.asObservable();

  constructor(
    @Inject(EMPLOYMENT_ACCESS_ENDPOINTS) private employmentAccessApiEndpoints: EmploymentAccessApiEndpoints,
    private apiService: ApiService
  ) {}

  fetchManagedEmploymentsAccessData(cachePerRoute = false, forceFetching = false): Observable<EmploymentAccess[]> {
    return this.fetchAccessData(
      this.managedEmploymentsAccessSubject,
      this.employmentAccessApiEndpoints.MANAGED_EMPLOYMENT_ACCESS(),
      cachePerRoute,
      forceFetching,
      transformManagedEmploymentsMap
    );
  }

  fetchMyEmploymentsAccessData(cachePerRoute = false, forceFetching = false): Observable<EmploymentAccess[]> {
    return this.fetchAccessData(
      this.myEmploymentsAccessSubject,
      this.employmentAccessApiEndpoints.MY_EMPLOYMENT_ACCESS(),
      cachePerRoute,
      forceFetching
    );
  }

  fetchAllAccessData(cachePerRoute = false, forceFetching = false): Observable<[EmploymentAccess[], EmploymentAccess[]]> {
    return forkJoin([
      this.fetchManagedEmploymentsAccessData(cachePerRoute, forceFetching),
      this.fetchMyEmploymentsAccessData(cachePerRoute, forceFetching),
    ]);
  }

  getManagedEmploymentsAccessData(): EmploymentAccess[] {
    return this.managedEmploymentsAccessSubject.getValue();
  }

  getManagedEmploymentsAccessDataByEmployeeAndCompany(employeeId: string, companyId: number): EmploymentAccess[] {
    return this.managedEmploymentsAccessSubject
      .getValue()
      .filter((employmentAccess: EmploymentAccess) => employmentAccess.companyId === companyId && employmentAccess.userId === employeeId);
  }

  getOwnEmploymentsAccessDataByEmployeeAndCompany(employeeId: string, companyId: number): EmploymentAccess[] {
    return this.myEmploymentsAccessSubject
      .getValue()
      .filter((employmentAccess: EmploymentAccess) => employmentAccess.companyId === companyId && employmentAccess.userId === employeeId);
  }

  getMyEmploymentsAccessData(): EmploymentAccess[] {
    return this.myEmploymentsAccessSubject.getValue();
  }

  getAllEmploymentsAccessData(): EmploymentAccess[] {
    return [...this.getManagedEmploymentsAccessData(), ...this.getMyEmploymentsAccessData()];
  }

  updateAccessData<T>(): MonoTypeOperatorFunction<T> {
    return (source: Observable<T>) =>
      source.pipe(
        switchMap((data: T) => forkJoin([of(data), this.fetchAllAccessData(false, true)]).pipe(map(([sourceData]) => sourceData)))
      );
  }

  private fetchAccessData(
    accessDataSubject: BehaviorSubject<EmploymentAccess[]>,
    endpoint: string,
    cachePerRoute,
    forceFetching,
    transformer: (data: EmploymentAccess[]) => EmploymentAccess[] = (data: EmploymentAccess[]) => data
  ): Observable<EmploymentAccess[]> {
    const currentFetchedAccessData = accessDataSubject.getValue();

    if (!forceFetching && Array.isArray(currentFetchedAccessData) && currentFetchedAccessData.length) {
      return of(currentFetchedAccessData);
    }

    return this.apiService.get(endpoint, {}, { cachePerRoute }).pipe(
      mapApiResponseItems<EmploymentAccess>(),
      tap((employmentAccessData: EmploymentAccess[]) => accessDataSubject.next(transformer(employmentAccessData)))
    );
  }
}
